import { Button } from "../../../shared/ui/button";
import { Title } from "../../../shared/ui/title";
import { Auth } from "../auth/auth";
import cx from "classnames";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";

import { useGenerateMutation } from "../../data-access/store/services/auth.service";
import { useEffect, useState } from "react";
import { setMessage } from "../../../shared/data-access/store/message/messageSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectInit,
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
import { useNavigate } from "react-router-dom";
import { checkTranslationKey } from "../../../utils/translation";
import { UpstreamServiceBanner } from "../../../shared/ui/upstream-service-banner";
import { Modal } from "../../../shared/ui/modal";
import { AlreadySubscribed } from "../../../shared/ui/modal-notifications/already-subscribed";

import "./subscribe.scss";

export function Subscribe() {
  const [msisdn, setMsisdn] = useState<string>("");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [
    generate,
    {
      isLoading: generateIsLoading,
      isSuccess: generateIsSuccess,
      error: generateError,
    },
  ] = useGenerateMutation();
  const init = useAppSelector(selectInit);
  const lang = useAppSelector(selectLanguage);
  const translations = useAppSelector(selectTranslations);
  const [showAlreadySubscribedModal, setShowAlreadySubscribedModal] =
    useState(false);

  useEffect(() => {
    if (generateError) {
      const errorMsg =
        (generateError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      const status = (generateError as { status?: number })?.status;
      const lower = errorMsg.toLowerCase();
      const isAlreadySubscribed =
        status === 409 ||
        lower.includes("already subscribed") ||
        lower.includes("already_subscribed") ||
        lower.includes("already a subscriber") ||
        lower.includes("subscription already");

      if (isAlreadySubscribed) {
        setShowAlreadySubscribedModal(true);
      } else if (errorMsg) {
        dispatch(setMessage({ content: errorMsg, type: "error" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateError]);

  useEffect(() => {
    if (generateIsSuccess) {
      navigate("/auth/pin", { state: msisdn });
    }
  }, [generateIsSuccess]);

  const prefix = init?.users?.msisdn?.prefix || "";
  const phoneMaxLength = init?.users?.msisdn?.maxLength || 9;
  const phoneMinLength = init?.users?.msisdn?.minLength || 9;

  // Africell-only MSISDNs, per MNO request. Edit this list to change allowed prefixes.
  const ALLOWED_MSISDN_PREFIXES = ["95", "96"];

  const SubscribeSchema = Yup.object().shape({
    phone_subscribe: Yup.string()
      .matches(
        /^[0-9]+$/,
        checkTranslationKey(
          translations[lang]?.The_phone_number_must_be_only_digits,
          "The phone number must be only digits"
        )
      )
      .matches(
        new RegExp(`^(${ALLOWED_MSISDN_PREFIXES.join("|")})`),
        checkTranslationKey(
          translations[lang]?.The_phone_number_must_be_valid_africell,
          `Please enter a valid number (starting with ${ALLOWED_MSISDN_PREFIXES.join(
            " or "
          )})`
        )
      )
      .min(
        phoneMinLength,
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_too_short,
          "The phone number is too short."
        )
      )
      .max(
        phoneMaxLength,
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_too_long,
          "The phone number is too long."
        )
      )
      .required(
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_required,
          "The phone number is required."
        )
      ),
  });

  return (
    <div className="auth-page subscribe-page">
      <Auth
        className="auth-shell-content"
        header={
          <Title backURL="/auth/sign-in">
            {checkTranslationKey(translations[lang]?.Subscribe, "Subscribe")}
          </Title>
        }
        hasFooter
      >
        <div className="form-title form-title-login">
          {checkTranslationKey(
            translations[lang]?.Create_your_account_and_subscribe,
            "Create your account and subscribe"
          )}
        </div>
        <div className="form-container">
          <Formik
            initialValues={{ phone_subscribe: "" }}
            validationSchema={SubscribeSchema}
            onSubmit={(values) => {
              setMsisdn(prefix + values.phone_subscribe);
              generate({ msisdn: prefix + values.phone_subscribe });
            }}
          >
            {({ errors, touched }) => (
              <>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Form {...({} as any)}>
                  <div
                    className={cx("form-input-row", {
                      danger: errors.phone_subscribe && touched.phone_subscribe,
                      success: !errors.phone_subscribe,
                    })}
                  >
                    <label className="label" htmlFor="phone_subscribe">
                      {checkTranslationKey(
                        translations[lang]?.Telephone_number_for_your_account,
                        "Telephone number for your account"
                      )}
                    </label>
                    <div className="prefix-input">
                      <span className="prefix">+{prefix}</span>
                      <Field
                        type="tel"
                        name="phone_subscribe"
                        className="half-input"
                        placeholder={checkTranslationKey(
                          translations[lang]?.Your_phone_number,
                          "Your phone number"
                        )}
                        autoComplete="off"
                        maxLength={phoneMaxLength}
                        required
                      />
                    </div>
                    <ErrorMessage name="phone_subscribe">
                      {(msg) => (
                        <div className="error-msg">
                          <img
                            src="/img/icns/alert-fill.svg"
                            alt="alert"
                            className="color-svg"
                          />
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>

                  <div>
                    <div className="submit">
                      <Button
                        buttonType="success"
                        type="submit"
                        value="Submit"
                        disabled={generateIsLoading}
                      >
                        {generateIsLoading
                          ? checkTranslationKey(
                              translations[lang]?.Submitting,
                              "Submitting..."
                            )
                          : checkTranslationKey(
                              translations[lang]?.Subscribe,
                              "Subscribe"
                            )}
                      </Button>
                      <Button
                        buttonType="white"
                        type="button"
                        value="Cancel"
                        onClick={() => navigate("/auth/sign-in")}
                        style={{ marginTop: "15px" }}
                      >
                        {checkTranslationKey(
                          translations[lang]?.Cancel,
                          "Cancel"
                        )}
                      </Button>
                    </div>
                    <p className="cost">
                      {checkTranslationKey(
                        translations[lang]?.Cost_per_day,
                        "Cost: 150 AOA/day"
                      )}
                    </p>
                    <p className="cost-free">
                      {checkTranslationKey(
                        translations[lang]?.First_day_free,
                        "1st day free"
                      )}
                    </p>
                    <div className="terms-area">
                      {/*{checkTranslationKey(*/}
                      {/*  translations[lang]?.You_will_soon_receive_a_PIN,*/}
                      {/*  "You will soon receive a password. By entering the password you confirm subscription and agree to the "*/}
                      {/*)}{" "}*/}
                      {checkTranslationKey(
                        translations[lang]?.register_the_service,
                        "To register for the service, you must accept the "
                      )}{" "}
                      <a href={init?.content?.terms[lang]} target="_blank">
                        {checkTranslationKey(
                          translations[lang]?.Terms_and_Conditions,
                          "Terms and Conditions"
                        )}
                      </a>{" "}
                      {checkTranslationKey(
                        translations[lang]?.confirm_age,
                        " and confirm that you are of legal age to do so."
                      )}{" "}
                    </div>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </div>
      </Auth>
      <div className="stream-service">
        <UpstreamServiceBanner />
      </div>

      <Modal
        show={showAlreadySubscribedModal}
        setShow={setShowAlreadySubscribedModal}
        hideCloseButton
      >
        <AlreadySubscribed
          onButtonClick={() => {
            setShowAlreadySubscribedModal(false);
            navigate("/");
          }}
        />
      </Modal>
    </div>
  );
}

export default Subscribe;
